import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.currentIndex = 0;
    this.isRendering = false;
    this.render();
    this.observeCartChanges();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  observeCartChanges() {
    const cartItemsTable = document.querySelector('.cart-items__table');
    if (!cartItemsTable) return;

    this.observer = new MutationObserver(() => {
      if (this.isRendering) return;
      this.currentIndex = 0;
      this.render();
    });

    this.observer.observe(cartItemsTable, { childList: true, subtree: true });
  }

  async render() {
    this.isRendering = true;
    this.innerHTML = '<p class="cart-upsell__loading">Loading recommendations…</p>';
    // ...rest of render() stays exactly the same as before

    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      if (!cart.items.length) {
        this.innerHTML = '';
        return;
      }

      const cartProductIds = [...new Set(cart.items.map((item) => item.product_id))];
      const excludeIds = new Set(cartProductIds);

      let recommendations = await this.collectRecommendations(cartProductIds, 'complementary', excludeIds);

      if (recommendations.length < 4) {
        const related = await this.collectRecommendations(cartProductIds, 'related', excludeIds);
        for (const product of related) {
          if (recommendations.length >= 4) break;
          if (!recommendations.find((p) => p.id === product.id)) {
            recommendations.push(product);
          }
        }
      }

      recommendations = recommendations.slice(0, 4);
      this.products = recommendations;

      if (!recommendations.length) {
        this.innerHTML = '';
        return;
      }

      this.innerHTML = `
        <p class="cart-upsell__title">Complete with</p>
        <div class="cart-upsell__slide" data-upsell-slide>
          ${this.renderProduct(recommendations[0])}
        </div>
        ${recommendations.length > 1 ? `
          <div class="cart-upsell__dots" data-upsell-dots>
            ${recommendations.map((_, i) => `
              <button type="button" class="cart-upsell__dot${i === 0 ? ' active' : ''}" data-upsell-dot data-index="${i}" aria-label="Show product ${i + 1}"></button>
            `).join('')}
          </div>
        ` : ''}
      `;

this.attachEvents();
    } catch (error) {
      console.error('Cart upsell error:', error);
      this.innerHTML = '';
    } finally {
      this.isRendering = false;
    }
  }

  attachEvents() {
    this.querySelectorAll('[data-upsell-dot]').forEach((dot) => {
      dot.addEventListener('click', () => {
        this.currentIndex = parseInt(dot.dataset.index, 10);
        this.updateSlide();
      });
    });
    this.querySelector('[data-upsell-add]')?.addEventListener('click', (event) => this.addToCart(event));
  }

  updateSlide() {
    const slideContainer = this.querySelector('[data-upsell-slide]');
    slideContainer.innerHTML = this.renderProduct(this.products[this.currentIndex]);
    this.querySelector('[data-upsell-add]')?.addEventListener('click', (event) => this.addToCart(event));

    this.querySelectorAll('[data-upsell-dot]').forEach((dot, i) => {
      dot.classList.toggle('active', i === this.currentIndex);
    });
  }

  async collectRecommendations(productIds, intent, excludeIds) {
    const results = [];
    for (const id of productIds) {
      if (results.length >= 4) break;
      try {
        const res = await fetch(`/recommendations/products.json?product_id=${id}&intent=${intent}&limit=4`);
        const data = await res.json();
        for (const product of data.products || []) {
          if (excludeIds.has(product.id)) continue;
          if (!results.find((p) => p.id === product.id)) {
            results.push(product);
          }
        }
      } catch (error) {
        // skip
      }
    }
    return results;
  }

  renderProduct(product) {
    const variant = product.variants[0];
    const image = product.featured_image || (product.images && product.images[0]) || '';

    return `
      <a href="${product.url}" class="cart-upsell__image">
        <img src="${image}" alt="${product.title}" loading="lazy">
      </a>
      <div class="cart-upsell__info">
        <a href="${product.url}" class="cart-upsell__product-title">${product.title}</a>
        <span class="cart-upsell__price">${this.formatMoney(variant.price)}</span>
        <button type="button" class="cart-upsell__add-button" data-upsell-add data-variant-id="${variant.id}">
          Add to cart
        </button>
      </div>
    `;
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    });
  }

async addToCart(event) {
  const button = event.currentTarget;
  const variantId = button.dataset.variantId;
  button.disabled = true;
  button.textContent = 'Adding…';

  // Include cart-items-component section IDs so the add response carries rendered
  // section HTML — this is what lets CartItemsComponent take its direct-morph fast
  // path instead of falling back to a slower, less reliable section refetch.
  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  this.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'standard-action',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferredEventPromise.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
      }),
    });

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.message || 'Add to cart request failed');
    }

    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();

    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cart-upsell',
        sourceId: this.id?.toString() || 'cart-upsell',
        itemCount: cart.item_count,
        sections: result.sections,
        didError: false,
      },
    });

    button.textContent = 'Added';
    setTimeout(() => {
      button.disabled = false;
      button.textContent = 'Add to cart';
    }, 1200);
  } catch (error) {
    console.error('Add to cart failed:', error);

    deferredEventPromise.reject(error);

    this.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Add to cart failed',
        code: 'SERVICE_UNAVAILABLE',
      })
    );

    button.disabled = false;
    button.textContent = 'Add to cart';
  }
}
}

customElements.define('cart-upsell-component', CartUpsell);